import { Component, Input, OnInit } from '@angular/core';
import { Subject } from 'rxjs';
import { debounceTime, map } from 'rxjs/operators';

import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';

import { AssociationService } from '../../../../../../../services/association/association.service';
import {
  CallbackResult,
  CallbackResultStatus,
  dateStringFromISO8601,
  getISO8601DateStringFromMoment,
  propExists,
} from '../../../../../../../utils';
import { Association, AssociationMember } from '../../../../../../../models/association';
import { CustomerSearchResult } from '../../../../../../../models/customer';
import { ListResponseCountOptional } from '../../../../../../../models/list-response';
import { AccountService } from '../../../../../../../services/account/account.service';
import { OrganizationConfigService } from '../../../../../../../services/organization-config/organization-config.service';
import { CustomerService } from '../../../../../../../services/customer/customer.service';
import * as moment from 'moment';


@Component({
  selector: 'app-add-association-member-modal',
  templateUrl: './add-association-member-modal.component.html',
  styleUrls: ['./add-association-member-modal.component.css']
})
export class AddAssociationMemberModalComponent implements OnInit {

  @Input() association: Association;
  @Input() excludeCustomerIds: number[];
  @Input() savedCallback: (result: CallbackResult) => undefined;

  public associationMember: AssociationMember = {
    id: null,
    url: null,
    association_id: null,
    customer_id: null,
    association_url: null,
    customer_url: null,
    association_name: null,
    association_verification_requirements: null,
    association_shows_reverification_alert: false,
    customer_first_name: null,
    customer_last_name: null,
    customer_birthday: null,
    last_reverified_at: null,
    is_pending_reverification: false,
    has_pending_application: false,
    application_is_hidden_from_inbox: false,
    application_document: null,
    application_text: '',
    approved_by_id: null,
    approved_by_first_name: null,
    approved_by_last_name: null,
    approved_at: null,
    created_at: null,
    updated_at: null
  };

  public loadingCustomers = false;
  public search = '';
  public customerFiltersChangedSubject = new Subject<null>();
  public customerList: ListResponseCountOptional<CustomerSearchResult>;

  public selectedCustomer: CustomerSearchResult = null;

  public loadingSubmit = false;
  public errors: any = {};
  public errorMessage = '';
  public serverError: number = null;

  // The number of customer list requests. Used to ensure that only the most
  // recent request is displayed, in the event of concurrent requests (due to,
  // e.g., filtering).
  private customerListRequestCount = 0;

  // Create instance references to lookup functions and enums so they may be
  // called in the template.
  public dateStringFromISO8601 = dateStringFromISO8601;

  private recentActivityFilter: string | undefined = undefined;

  constructor(
    public accountService: AccountService,
    public activeModal: NgbActiveModal,
    public orgConfigService: OrganizationConfigService,
    private customerService: CustomerService,
    private associationService: AssociationService
  ) {}

  ngOnInit() {
    if (!this.accountService.getMyAccount().can_manage_members) {
      return;
    }

    this.associationMember.association_id = this.association.id;

    // Handle changes to the customer filters by reloading the customers,
    // subject to debounce.
    this.customerFiltersChangedSubject.pipe(
      map(() => { this.loadingCustomers = true; }),
      // Avoid rapid requests by waiting 300ms before applying the filters.
      debounceTime(300)
    ).subscribe(() => {
      this.loadCustomers();
    });
  }

  /**
   * Handle a change in the customer filters. The list of customers will be
   * filtered according to the new values.
   */
  public handleCustomerFilterChange() {
    // Reset the selected customer.
    this.selectedCustomer = null;

    // Reset errors.
    this.errorMessage = '';
    this.errors = {};

    const orgConfig = this.orgConfigService.getOrganizationConfig();
    const mustApplyRecentActivityFilter = (
      orgConfig &&
      orgConfig.customer_search_recent_activity_window_months
    );
    if (mustApplyRecentActivityFilter) {
      this.recentActivityFilter = getISO8601DateStringFromMoment(
        moment().subtract(orgConfig.customer_search_recent_activity_window_months, 'months')
      );
    }

    this.customerFiltersChangedSubject.next();
  }

  /**
   * Load the page of customers corresponding to the current page and filter
   * selections.
   */
  public loadCustomers() {
    this.loadingCustomers = true;

    // Increment the request count and save a local copy.
    this.customerListRequestCount++;
    const thisRequestCount = this.customerListRequestCount;

    if (!this.hasSearchInput()) {
      this.customerList = null;
      this.loadingCustomers = false;
      return;
    }

    this.customerService
      .searchCustomers(
        1,
        200,
        {
          search: this.search,
          last_activity_timestamp_min: this.recentActivityFilter,
        },
        true
      )
      .then(response => {
        // Only update the customer list if there has not been a more recent
        // request initiated.
        if (thisRequestCount === this.customerListRequestCount) {
          this.customerList = response;
        }
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => {
        if (thisRequestCount === this.customerListRequestCount) {
          this.loadingCustomers = false;
        }
      });
  }

  /**
   * Whether a non-zero number of customer results have been loaded.
   */
  public customerResultsPresent(): boolean {
    return (
      this.customerList &&
      this.customerList.results &&
      (this.customerList.results.length > 0)
    );
  }

  public filteredCustomerResults(): CustomerSearchResult[] {
    return this.customerList.results.filter(
      customer => !this.excludeCustomerIds.includes(customer.id)
    );
  }

  /**
   * Handle a click on one of the customer results by displaying the customer's
   * name in the search input.
   */
  public selectCustomer(customer: CustomerSearchResult) {
    this.selectedCustomer = customer;
    this.search = (
      customer.first_name + ' ' + customer.last_name
    );
  }

  /**
   * Whether a valid set of inputs have been selected.
   * Used to determine whether the save button is clickable.
   */
  public validInputSelected() {
    return this.selectedCustomer !== null;
  }

  public handleSave() {
    this.loadingSubmit = true;

    this.errorMessage = '';
    this.errors = {};

    this.associationMember.customer_id = this.selectedCustomer.id;

    this.associationService
      .addAssociationMember(this.associationMember)
      .then(newMember => {
        this.savedCallback({
          text: 'Member added successfully.',
          status: CallbackResultStatus.success
        });
        this.activeModal.close();
      })
      .catch(reason => {
        if (reason.status === 400) {
          this.errors = reason.error;
          this.errorMessage = 'Please correct the errors above and try again.';
        } else if (reason.status === 403) {
          this.errorMessage = 'You do not have permission to add this member.';
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingSubmit = false; });
  }

  public hasSearchInput() {
    return this.search.trim().length >= 3;
  }

  public hasRecentActivityFilter(): boolean {
    return propExists(this.recentActivityFilter);
  }

  public handleExpandSearch() {
    this.recentActivityFilter = undefined;
    this.loadingCustomers = true;
    this.customerFiltersChangedSubject.next();
  }

}
